// react imports
import axios from "axios";
import { useEffect, useState } from "react";
import { Pagination, Table } from "antd";
import { ColumnsType } from "antd/lib/table";

// common imports
import { API_URL } from "common/constants/config";
import GarbageIcon from "common/svg/GarbageIcon";

// styled-components imports
import DeleteButton from "./styled-components/DeleteButton";
import FootprintValue from "./styled-components/FootprintValue";
import ResultText from "./styled-components/ResultText";

import CategoryHeader from "./CategoryHeader";
import "./css/Pagination.css";
import "./css/Table.css";

function Results() {
  interface Result {
    id: string;
    email: string;
    transport: number;
    energy: number;
    nutrition: number;
    householdHabits: number;
  }

  interface ResultData extends Omit<Result, "id"> {
    key: string;
  }

  const [results, setResults] = useState<ResultData[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [count, setCount] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useState<string>("");

  const columns: ColumnsType<ResultData> = [
    {
      title: "Correo electrónico",
      key: "email",
      dataIndex: "email",
      align: "center",
    },
    {
      title: "Movilidad",
      key: "transport",
      dataIndex: "transport",
      align: "center",
      render: (value) => (
        <ResultText>
          <FootprintValue>{Number(value).toFixed(2)}</FootprintValue>
          &nbsp;
          <span>
            tmCO<sub>2</sub>
          </span>
        </ResultText>
      ),
    },
    {
      title: "Energía",
      key: "energy",
      dataIndex: "energy",
      align: "center",
      render: (value) => (
        <ResultText>
          <FootprintValue>{Number(value).toFixed(2)}</FootprintValue>
          &nbsp;
          <span>
            tmCO<sub>2</sub>
          </span>
        </ResultText>
      ),
    },
    {
      title: "Alimentación",
      key: "nutrition",
      dataIndex: "nutrition",
      align: "center",
      render: (value) => (
        <ResultText>
          <FootprintValue>{Number(value).toFixed(2)}</FootprintValue>
          &nbsp;
          <span>
            tmCO<sub>2</sub>
          </span>
        </ResultText>
      ),
    },
    {
      title: "Hábitos de hogar",
      key: "householdHabits",
      dataIndex: "householdHabits",
      align: "center",
      render: (value) => (
        <ResultText>
          <FootprintValue>{Number(value).toFixed(2)}</FootprintValue>
          &nbsp;
          <span>
            tmCO<sub>2</sub>
          </span>
        </ResultText>
      ),
    },
    {
      title: "Eliminar",
      align: "center",
      render: (value) => (
        <DeleteButton onClick={deleteResult(value.key)}>
          <GarbageIcon />
        </DeleteButton>
      ),
    },
  ];

  function handleCountSelect(value: number) {
    setCount(value);
  }

  function handleQueryChange(value: string) {
    setQuery(value);
  }

  function handlePageChange(value: number) {
    setPage(value);
  }

  function deleteResult(id: string) {
    return async () => {
      const response = await axios.delete(`${API_URL}/admin/results/${id}`);
      if (response.status === 200) {
        setResults(results.filter((result) => result.key !== id));
        setTotal(total - 1);
      }
    };
  }

  async function fetchResults() {
    const {
      data: { results: resultsResponse, total: totalResponse },
    } = await axios.get(`${API_URL}/admin/results`, {
      params: {
        count,
        page,
        q: query,
      },
    });

    const mappedResults: ResultData[] = resultsResponse.map(
      ({ id, ...result }: Result) => ({
        key: id,
        ...result,
      })
    );

    setResults(mappedResults);
    setTotal(totalResponse);
  }

  useEffect(() => {
    fetchResults();
  }, [count, page, query]); // eslint-disable-line

  return (
    <>
      <CategoryHeader
        title="Resultados"
        onSelect={handleCountSelect}
        onSearch={handleQueryChange}
      />
      <Table columns={columns} dataSource={results} pagination={false} />
      <Pagination
        current={page}
        pageSize={count}
        total={total}
        showSizeChanger={false}
        onChange={handlePageChange}
      />
    </>
  );
}

export default Results;
